import type { PreviousYearTotalItem } from "@playlive/fundraiser-data";
import { usePreviousYearTotals } from "@playlive/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import useTiltifyConfig from "@/hooks/useTiltifyConfig";

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Amounts arrive as decimal strings; sum in cents so totals don't pick up float drift. */
function toCents(amount: string | null | undefined): number {
  const parsed = Number.parseFloat(amount ?? "");
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function formatCents(cents: number): string {
  return usdFormatter.format(cents / 100);
}

type YearCampaign = {
  id: string;
  name: string;
  cents: number;
  fundraisingEventName: string | null;
};

type YearGroup = {
  year: string;
  campaigns: YearCampaign[];
  cents: number;
};

/**
 * Collapses the flat `PreviousYearTotalItem[]` into one entry per year —
 * newest first, campaigns sorted biggest-first, totals summed in cents.
 */
function groupByYear(rows: PreviousYearTotalItem[] | undefined): YearGroup[] {
  const byYear = new Map<string, YearGroup>();

  for (const row of rows ?? []) {
    const cents = toCents(row.amount);
    const campaign: YearCampaign = {
      id: row.id,
      // Event-level fallback rows come back with an empty name.
      name: row.name?.trim() || `${row.year} Fundraising Event`,
      cents,
      fundraisingEventName: row.fundraisingEventName,
    };
    const group = byYear.get(row.year);

    if (group) {
      group.campaigns.push(campaign);
      group.cents += cents;
    } else {
      byYear.set(row.year, { year: row.year, campaigns: [campaign], cents });
    }
  }

  for (const group of byYear.values()) {
    group.campaigns.sort((a, b) => b.cents - a.cents);
  }

  return [...byYear.values()].sort((a, b) => Number(b.year) - Number(a.year));
}

/** Alternating sides: even entries hang off the left rail, odd ones off the right. */
function sideClasses(index: number): string {
  return index % 2 === 0 ? "timeline-start md:text-end" : "timeline-end";
}

/** Entries slide in from their own side of the rail, staggered down the list. */
function entranceClasses(index: number): string {
  const direction = index % 2 === 0 ? "animate-fade-in-start" : "animate-fade-in-end";
  return `${direction} motion-reduce:animate-none`;
}

/** Capped so a long history doesn't leave the last year waiting seconds to appear. */
function entranceDelay(index: number) {
  return { animationDelay: `${Math.min(index, 8) * 70}ms` };
}

function TimelineDot({ isCurrent = false, index = 0 }: { isCurrent?: boolean; index?: number }) {
  return (
    <div className="timeline-middle">
      <span
        style={entranceDelay(index)}
        className={`block size-3 rounded-full ring-4 ring-base-100 animate-fade-in motion-reduce:animate-none ${
          isCurrent ? "bg-primary" : "bg-base-300"
        }`}
      />
    </div>
  );
}

/**
 * One-line text with a daisyUI tooltip carrying the full value, shown only
 * when the text is actually clipped.
 *
 * The tooltip sits on a wrapper rather than the truncating span itself:
 * `truncate` implies `overflow: hidden`, which would clip the tooltip bubble.
 */
function TruncatedText({ text, className = "" }: { text: string; className?: string }) {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    const measure = () => {
      // +1 absorbs sub-pixel rounding in the layout engine.
      setIsTruncated(text.length > 0 && element.scrollWidth > element.clientWidth + 1);
    };

    measure();

    // Not implemented in jsdom, so tests fall back to the one-shot measure.
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [text]);

  return (
    <span className={`block max-w-full text-start ${isTruncated ? "tooltip tooltip-top" : ""}`}>
      {/* daisyUI centers and wraps `data-tip` bubbles, so render the tip as a
          `tooltip-content` child instead — it takes utility classes. */}
      {isTruncated && (
        <span className="tooltip-content max-w-none whitespace-nowrap text-start">{text}</span>
      )}
      <span ref={textRef} className={`block truncate ${className}`}>
        {text}
      </span>
    </span>
  );
}

function YearEntry({
  group,
  index,
  isFirst,
  isLast,
  isCurrent,
}: {
  group: YearGroup;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isCurrent: boolean;
}) {
  const showBreakdown = group.campaigns.length > 1;

  return (
    <li>
      {!isFirst && <hr className="bg-base-300" />}

      <TimelineDot isCurrent={isCurrent} index={index} />

      <div
        style={entranceDelay(index)}
        className={`${sideClasses(index)} ${entranceClasses(index)} timeline-box mb-10 w-full max-w-sm border-base-300 bg-base-200 shadow-sm`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <time
            dateTime={group.year}
            className="text-2xl font-thin leading-none tabular-nums opacity-40"
          >
            {group.year}
          </time>
          <span className="text-lg font-semibold tabular-nums text-success">
            {formatCents(group.cents)}
          </span>
        </div>

        <ul className="mt-3 space-y-1 text-start">
          {group.campaigns.map((campaign) => (
            <li key={campaign.id} className="flex items-baseline justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-col">
                <TruncatedText text={campaign.name} className="text-sm" />
                {campaign.fundraisingEventName && (
                  <TruncatedText
                    text={campaign.fundraisingEventName}
                    className="mb-2 text-xs opacity-60"
                  />
                )}
              </div>
              {showBreakdown && (
                <span className="shrink-0 text-sm tabular-nums opacity-60">
                  {formatCents(campaign.cents)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {!isLast && <hr className="bg-base-300" />}
    </li>
  );
}

function SkeletonEntry({ index, isFirst, isLast }: SkeletonEntryProps) {
  return (
    <li>
      {!isFirst && <hr className="bg-base-300" />}

      <TimelineDot index={index} />

      <div
        style={entranceDelay(index)}
        className={`${sideClasses(index)} ${entranceClasses(index)} timeline-box mb-10 w-full max-w-sm border-base-300 bg-base-100`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="skeleton h-7 w-16" />
          <div className="skeleton h-6 w-24" />
        </div>
        <div className="mt-3 space-y-2">
          <div className="skeleton h-4 w-full" />
          <div className="skeleton h-4 w-2/3" />
        </div>
      </div>

      {!isLast && <hr className="bg-base-300" />}
    </li>
  );
}

type SkeletonEntryProps = {
  index: number;
  isFirst: boolean;
  isLast: boolean;
};

export function TiltifyPreviousYearsTotals({
  overrideUserSlug,
}: {
  overrideUserSlug?: string | null;
}) {
  const { config: tiltifyConfig } = useTiltifyConfig();

  // Empty slug (signed out) disables the query. Callers gate on auth before
  // rendering this, so there is nothing to show without one.
  const slug = (overrideUserSlug || tiltifyConfig.tiltifyUserSlug || "").trim() || null;

  const { data, isLoading, error } = usePreviousYearTotals({ slug, isTeam: false });

  const years = useMemo(() => groupByYear(data), [data]);

  const campaignCount = useMemo(
    () => years.reduce((sum, group) => sum + group.campaigns.length, 0),
    [years],
  );

  // Marks the in-progress year on the rail; everything below it is finished.
  const currentYear = String(new Date().getFullYear());

  const skeletonKeys = ["a", "b", "c"];

  return (
    // Width is viewport-relative on purpose: the page's `drawer-content` is a
    // centered flex column, so a percentage width would shrink-wrap the
    // content and make the loading state narrower than the loaded one.
    <section className="mx-auto my-6 w-3xl max-w-[calc(100vw-2rem)]">
      <div className="mb-2 text-center">
        <h2 className="text-xl font-semibold leading-tight">Previous Years Totals</h2>
        {slug && isLoading ? (
          <div className="skeleton mx-auto mt-1 h-4 w-40" />
        ) : (
          years.length > 0 && (
            <p className="animate-fade-in select-none text-sm opacity-60 motion-reduce:animate-none">
              {years.length} {years.length === 1 ? "year" : "years"} · {campaignCount}{" "}
              {campaignCount === 1 ? "campaign" : "campaigns"}
            </p>
          )
        )}
      </div>

      <div className="py-6">
        {slug && isLoading && (
          <ul className="timeline timeline-vertical timeline-snap-icon w-full max-md:timeline-compact">
            {skeletonKeys.map((key, index) => (
              <SkeletonEntry
                key={key}
                index={index}
                isFirst={index === 0}
                isLast={index === skeletonKeys.length - 1}
              />
            ))}
          </ul>
        )}

        {slug && !isLoading && error && (
          <p className="py-4 text-center text-sm text-error">
            Couldn&apos;t load previous years totals.
          </p>
        )}

        {slug && !isLoading && !error && years.length === 0 && (
          <p className="py-4 text-center text-sm opacity-60">No previous years to show yet.</p>
        )}

        {slug && !isLoading && !error && years.length > 0 && (
          <ul className="timeline timeline-vertical timeline-snap-icon w-full max-md:timeline-compact">
            {years.map((group, index) => (
              <YearEntry
                key={group.year}
                group={group}
                index={index}
                isFirst={index === 0}
                isLast={index === years.length - 1}
                isCurrent={group.year === currentYear}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
